import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import urlParser from 'url';
import core from '@bravissimolabs/website-core';
import Meta from 'shared/containers/MetaContainer';
import EditorialContainer from 'brastrap/editorial/editorial-container/EditorialContainer';
import { setContent } from '../../../actions/editorial';
import { apiGetPage } from '../../../actions/page';
import { setSchema } from '../../../actions/schema';
import generateFaqSchema from '../../HelpSection/faqSchemaGenerator/faqSchemaGenerator';
import { startChat } from '../../../actions/live-help';
import { HOMEPAGE_LIVECHAT_ROUTE } from '../home.js';

export const EditorialContentPage = props => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (props.shouldFetchData) {
      dispatch(apiGetPage(props.slug));
    }
  }, []);

  useEffect(() => {
    if (props.shouldStartChat) {
      const startChatSession = () => {
        if (window.brandembassy) {
          startChat(new Event('startChatOnload'));
        } else {
          setTimeout(startChatSession, 500);
        }
      };
      startChatSession();
    }
  }, [props.shouldStartChat]);

  // ECOM-422: Schema markup for FAQs on Editorial Content
  // In order to allow Editorial Content to benefit from FAQ schema markup,
  // and to allow it to be Content manageable, it must be contained in
  // Contentful in the following structure:
  // Page -> Container with slug 'faqs' -> Content Group -> Content Block (HelpContent)
  //
  // This is a workaround due to limitations with new Content Types given our
  // current license with Contentful. This should be revisted and improved with
  // custom content types if/when the licence is improved.
  if (
    props?.containers.find(container => container.slug === 'faqs')?.rows[0]
      ?.items[0]?.content?.faqLinks
  ) {
    const faqSchema = generateFaqSchema(
      props.containers.find(container => container.slug === 'faqs').rows[0]
        .items[0].content.faqLinks
    );

    props.schemas.push(faqSchema);
  }

  return (
    <Meta
      hideCanonicalTag={props.slug === 'error'}
      title={props.pageTitle || props.title}
      description={props.pageDescription}
      schemas={props.schemas}
      doNotIndex={props.doNotIndex}
    >
      <EditorialContainer {...props} />
    </Meta>
  );
};

EditorialContentPage.populateStore = async (store, props, req, res) => {
  const editorialApi = core.content.editorial;
  const { basePath } = store.getState().app.config;

  let slug = (urlParser.parse(req.url).pathname || '')
    .replace(basePath, '')
    .replace(/\/$/, '');

  const shouldStartChat = slug === HOMEPAGE_LIVECHAT_ROUTE;
  if (shouldStartChat) {
    slug = '';
  }

  // Don't query the editorial framework for static assets, ie: styles, favicons etc.
  const isStaticAsset = (slug || '').indexOf('static/') === 0;
  if (!isStaticAsset) {
    const content = await editorialApi.getEditorialContent(slug);
    const hasContent = content && Object.keys(content).length > 0;

    if (!slug) {
      // On homepage
      const url = `${req.protocol}://${req.headers.host}${
        store.getState().app.config.basePath
      }`;
      const organisationSchema = await core.content.schema.get('organisation', {
        url,
      });

      const siteSearchSchema = await core.content.schema.get('siteSearch', {
        url,
      });

      if (organisationSchema) {
        store.dispatch(setSchema('organisation', organisationSchema));
      }

      if (siteSearchSchema) {
        store.dispatch(setSchema('siteSearch', siteSearchSchema));
      }
    }

    if (!hasContent) {
      const redirectionSlug = await editorialApi.getSlugForAlias(slug);
      if (redirectionSlug) {
        // Redirect the url to the actual slug url if current page slug is alias
        // and return status code 301
        res.status(301);
        res.locals.redirectUrl = redirectionSlug;
      } else {
        const errorCode =
          process.env.NODE_ENV !== 'production' &&
          req.query &&
          req.query.errorCode;
        throw new Error(errorCode || 404);
      }
    } else {
      store.dispatch(setContent(content, shouldStartChat));
    }
  }
};

EditorialContentPage.defaulProps = {
  shouldFetchData: false,
  shouldStartChat: false,
};

EditorialContentPage.propTypes = {
  title: PropTypes.string,
  pageTitle: PropTypes.string,
  pageDescription: PropTypes.string,
  slug: PropTypes.string,
  schemas: PropTypes.array,
  talkativeTemplate: PropTypes.object,
  containers: PropTypes.array,
  keywords: PropTypes.array,
  shouldFetchData: PropTypes.bool,
  shouldStartChat: PropTypes.bool,
  doNotIndex: PropTypes.bool,
};

const mapStateToProps = state => {
  const schemas = [];

  if (state.schema.organisation) {
    schemas.push(state.schema.organisation);
  }

  if (state.schema.siteSearch) {
    schemas.push(state.schema.siteSearch);
  }

  return {
    ...state.content.data,
    schemas,
    shouldFetchData: state.content && Object.keys(state.content).length > 0,
    shouldStartChat: state.content && state.content.shouldStartChat,
  };
};

export default connect(mapStateToProps)(EditorialContentPage);
